/**
 * UnifiedInboxDropdown
 *
 * Dropdown component for the header showing recent unified inbox messages
 * (both platform and application messages) with unread count badge.
 */

import { Fragment, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { BellIcon, InboxIcon, CheckIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useUnifiedInbox } from '../hooks/useUnifiedInbox';
import { UnifiedInboxMessageItem } from './UnifiedInboxMessageItem';
import type { UnifiedInboxItem } from '../../../types/app-message.types';
import { Spinner } from '../../../components/ui/Spinner';

export function UnifiedInboxDropdown() {
  const navigate = useNavigate();
  const {
    items,
    totalUnreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    archiveMessage,
    refresh,
  } = useUnifiedInbox();

  const [isOpen, setIsOpen] = useState(false);

  // Show only first 5 items in dropdown
  const recentItems = items.slice(0, 5);

  const handleMarkAsRead = async (item: UnifiedInboxItem) => {
    await markAsRead(item);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleArchive = async (item: UnifiedInboxItem) => {
    await archiveMessage(item);
  };

  const handleItemClick = (item: UnifiedInboxItem) => {
    // Navigate based on action URL or to inbox page
    if (item.actionUrl) {
      if (item.actionUrl.startsWith('/')) {
        navigate(item.actionUrl);
      } else {
        window.open(item.actionUrl, '_blank', 'noopener,noreferrer');
      }
    } else {
      // Navigate to inbox page for viewing full message
      navigate('/app/inbox');
    }
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
    refresh();
  };

  return (
    <Menu as="div" className="relative">
      <Menu.Button
        onClick={handleOpen}
        className="relative p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <span className="sr-only">View notifications</span>
        <BellIcon className="h-6 w-6" />
        {totalUnreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
              {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
            </span>
          </span>
        )}
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-96 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2">
              <InboxIcon className="h-5 w-5 text-gray-500" />
              <span className="font-semibold text-gray-900">Notifications</span>
              {totalUnreadCount > 0 && (
                <span className="text-xs font-medium text-gray-500">
                  ({totalUnreadCount} unread)
                </span>
              )}
            </div>
            {totalUnreadCount > 0 && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleMarkAllAsRead();
                }}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium"
              >
                <CheckIcon className="h-4 w-4" />
                Mark all read
              </button>
            )}
          </div>

          {/* Messages */}
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {isLoading && recentItems.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="md" />
              </div>
            ) : recentItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <BellIcon className="h-10 w-10 mb-2 text-gray-300" />
                <p className="text-sm font-medium">No notifications</p>
                <p className="text-xs">You're all caught up!</p>
              </div>
            ) : (
              <Menu.Items static>
                {recentItems.map((item) => (
                  <Menu.Item key={item.id}>
                    {({ active }) => (
                      <div className={clsx(active && 'bg-gray-50')}>
                        <UnifiedInboxMessageItem
                          item={item}
                          onRead={handleMarkAsRead}
                          onArchive={handleArchive}
                          onClick={handleItemClick}
                          showActions={false}
                        />
                      </div>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-gray-50">
            <Link
              to="/app/inbox"
              className="block px-4 py-3 text-sm text-center text-primary-600 hover:text-primary-800 font-medium hover:bg-gray-100 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              View all notifications
            </Link>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

export default UnifiedInboxDropdown;
